'use client';

import { useLanguage } from './LanguageProvider';
import { useTranslation } from '@/hooks/useTranslation';
import Image from 'next/image';
import { useState, useRef } from 'react';
import { useClickOutside } from '@/hooks/useClickOutside';
import { EnabledLanguage } from '@/types/translations';
import { useAuth } from '@/components/auth/AuthProvider';
import { UserService } from '@/services/user';

const languages = {
  en: {
    name: 'English',
    flag: '/images/flags/en.png'
  },
  es: {
    name: 'Español',
    flag: '/images/flags/es.png'
  },
  pt: {
    name: 'Português',
    flag: '/images/flags/pt.png'
  },
  eu: {
    name: 'Euskara',
    flag: '/images/flags/eu.png'
  },
  ca: {
    name: 'Català',
    flag: '/images/flags/ca.png'
  },
  fr: {
    name: 'Français',
    flag: '/images/flags/fr.png'
  },
  it: {
    name: 'Italiano',
    flag: '/images/flags/it.png'
  }
} as const;

interface LanguageSelectorProps {
  availableLanguages?: string[];
  // Cuando true el desplegable se abre hacia ARRIBA (bottom-full) en vez de hacia abajo.
  // Útil en el footer del microsite, donde abrir hacia abajo obligaría a hacer scroll.
  openUp?: boolean;
  // Estilos extra para el botón disparador (p. ej. colores claros en el nav oscuro).
  buttonClassName?: string;
  // Alineación horizontal del menú. Por defecto el offset del footer; el nav pasa "right-0".
  menuClassName?: string;
}

export default function LanguageSelector({
  availableLanguages,
  openUp = false,
  buttonClassName = '',
  menuClassName = '-right-[84px]',
}: LanguageSelectorProps = {}) {
  const { currentLocale, changeLocale } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const visibleLanguages = (
    availableLanguages
      ? Object.fromEntries(
          Object.entries(languages).filter(([code]) => availableLanguages.includes(code))
        )
      : languages
  ) as Record<string, { name: string; flag: string }>;

  useClickOutside(dropdownRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  const handleLanguageChange = (locale: EnabledLanguage) => {
    changeLocale(locale);
    setIsOpen(false);

    // Keep the athlete's Cosmos document (info.extra.webLanguage) in sync with the
    // selected web language when the user is logged in. Fire-and-forget.
    if (isAuthenticated) {
      UserService.getInstance()
        .updateWebLanguage(locale)
        .catch(err => console.error('Error al sincronizar el idioma web:', err));
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className={`flex flex-row items-center gap-2 w-[48px] ${buttonClassName}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={String(t('nav.language'))}
      >
        <Image
          src={languages[currentLocale as keyof typeof languages]?.flag}
          alt={languages[currentLocale as keyof typeof languages]?.name}
          width={20}
          height={15}
          className="rounded-sm"
        />

        <p className="text-sm font-medium">
          {currentLocale.toUpperCase()}
        </p>
      </button>

      {isOpen && (
        <div className={`dropdown-menu min-w-[120px] ${menuClassName} ${openUp ? 'top-auto bottom-full mt-0 mb-2 origin-bottom-right' : ''}`}>
          {Object.entries(visibleLanguages).map(([code, lang]) => (
            <button
              key={code}
              className={`dropdown-item w-full text-left flex items-center justify-between ${currentLocale === code ? 'bg-accent/50' : ''
                }`}
              onClick={() => handleLanguageChange(code as EnabledLanguage)}
            >
              <div className="flex items-center">
                <Image
                  src={lang.flag}
                  alt={lang.name}
                  width={16}
                  height={12}
                  className="rounded-sm mr-2"
                />
                <span>{lang.name}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
} 